import React, { useEffect, useId, useState, useRef } from 'react';
import clsx from 'clsx';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { StylesInspectorButton } from '../StylesInspectorButton/StylesInspectorButton';
import type { Button } from '@builder4/shared/types/styles';
import type { ButtonSize } from '@builder4/shared/styles/constants';
import { getButtonStyleFromData } from '@builder4/shared/utils/styleHelpers';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle, selectEditingStyleColors } from '@/store/globalStyles/selectors';
import { useButtonScale } from '@/hooks/useButtonScale';

interface StylesListItemButtonProps {
  className?: string;
  text?: string;
  /** When false, hide the Craft editor dropdown to allow rendering outside Editor context. */
  enableEditing?: boolean;
  onUpdate: (data: Button) => void;
  currentData: Button;
  selectedSize?: ButtonSize;
  onSizeChange?: (size: ButtonSize) => void;
}

export const StylesListItemButton: React.FC<StylesListItemButtonProps> = ({
  className = 'max-w-[216px]',
  text = 'Primary',
  enableEditing = true,
  onUpdate,
  currentData,
  selectedSize = 'medium',
  onSizeChange,
}) => {
  const [open, setOpen] = useState(false);
  const [currentSelectedSize, setCurrentSelectedSize] = useState<ButtonSize>(selectedSize);
  const instanceId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);
  const editingStyleColors = useAppSelector(selectEditingStyleColors);
  const colorsForPreview = editingStyleColors ?? activeGlobalStyle.colors;

  useEffect(() => {
    setCurrentSelectedSize(selectedSize);
  }, [selectedSize]);

  const handleSizeChange = (size: ButtonSize) => {
    setCurrentSelectedSize(size);
    onSizeChange?.(size);
  };
  const dropdownContentClass =
    'bg-neutral-0 relative z-10 flex w-64 flex-col overflow-hidden rounded-lg p-0 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100';

  const currentSizeData = currentData[currentSelectedSize]?.padding;
  const borderWidth = currentData.borderWidth;
  const scale = useButtonScale(containerRef, buttonRef, 4, [
    currentSelectedSize,
    currentSizeData,
    borderWidth,
  ]);

  const getButtonStyle = (button: Button): string => {
    return getButtonStyleFromData(
      button,
      currentSelectedSize,
      colorsForPreview,
      activeGlobalStyle.buttons.fontSizeUnit || 'px',
    );
  };

  const buttonStyle = getButtonStyle(currentData);

  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      const id = ev.detail?.id;
      // Ignore global scroll-close broadcasts so scrolling elsewhere doesn't close this inspector
      if (id === '__scroll_close__') return;
      if (id !== instanceId) setOpen(false);
    };
    window.addEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
    return () => window.removeEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
  }, [instanceId]);
  return (
    <div className={clsx('flex flex-col items-center gap-3 pb-3', className)}>
      <div className={clsx('flex w-full items-center justify-between px-2 py-0.5')}>
        {text && <span className="text-button-sm font-medium text-neutral-500">{text}</span>}

        <div className="flex items-center gap-1">
          {enableEditing && (
            <DropdownMenu
              open={open}
              onOpenChange={(v) => {
                if (v) {
                  try {
                    window.dispatchEvent(
                      new CustomEvent('lp:styles-edit-open', { detail: { id: instanceId } }),
                    );
                  } catch {
                    /* noop */
                  }
                }
                setOpen(v);
              }}
            >
              <DropdownMenuTrigger asChild>
                <SecondaryButton size="sm" state="default" text="Edit" icon={null} />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={dropdownContentClass}
                side="right"
                align="center"
                sideOffset={16}
                collisionPadding={{ top: 4, bottom: 4 }}
                onWheelCapture={(e) => e.stopPropagation()}
                style={{ maxHeight: 480 }}
              >
                <div className="bg-neutral-0 sticky top-0 z-10 flex h-10 min-h-10 w-full items-center justify-between border-b border-neutral-100 px-3 py-2">
                  <p className="text-button-sm text-neutral-900">{text || 'Button'}</p>
                  <IconSimpleButton
                    icon={IconCancel}
                    size={24}
                    state="default"
                    onClick={() => setOpen(false)}
                  />
                </div>
                <div
                  className="no-scrollbar w-full overflow-y-auto overscroll-contain"
                  style={{ height: 440 }}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <StylesInspectorButton
                    className="min-w-full"
                    text={''}
                    currentData={currentData}
                    onUpdate={onUpdate}
                    onSizeChange={handleSizeChange}
                  />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div
        ref={containerRef}
        className="relative mx-2 flex h-16 w-full items-center justify-center overflow-hidden rounded-md bg-neutral-100"
        style={{ padding: 4 }}
      >
        {/* Scale indicator - only show when scaled below 100% */}
        {scale < 0.99 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute top-1 right-1 z-10 cursor-help rounded bg-neutral-100 px-1.5 py-0.5">
                  <span className="text-[10px] leading-none font-medium text-neutral-900">
                    {Math.round(scale * 100)}%
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-white">
                <span>Zoom Out {Math.round(scale * 100)}%</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <div
          style={{
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center center',
            willChange: 'transform',
            width: 'fit-content',
            height: 'fit-content',
            position: 'absolute',
            top: '50%',
            left: '50%',
          }}
        >
          <div
            ref={buttonRef}
            className={clsx('inline-flex items-center justify-center select-none', buttonStyle)}
            role="presentation"
            aria-hidden="true"
            tabIndex={-1}
            style={{
              cursor: 'default',
            }}
          >
            <span className="leading-none">{text}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
